import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Pencil, Trash2, Plus, Layers } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { Loader, EmptyState, PageHeader } from "@/components/common/Primitives";
import { ConfirmButton } from "@/components/common/ConfirmButton";

export default function Categories() {
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/restaurant/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const wrap = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const add = (e) => {
    e.preventDefault();
    wrap(() => api.post("/restaurant/categories", { name, is_active: true }), "Category added").then(() => setName(""));
  };

  if (!items) return <Loader />;
  return (
    <div>
      <PageHeader title="Categories" subtitle="Group your menu items. Order here is the order customers see." />
      <form onSubmit={add} className="mb-6 flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Starters" required className="max-w-xs" data-testid="category-name-input" />
        <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600" data-testid="category-add-btn"><Plus className="mr-1 h-4 w-4" />Add</Button>
      </form>
      {items.length === 0 ? <EmptyState icon={Layers} title="No categories" description="Create your first category to start adding items." /> : (
        <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="categories-list">
          {items.map((c, i) => (
            <div key={c.id} className="flex items-center gap-3 px-4 py-3" data-testid={`category-row-${c.id}`}>
              <div className="flex flex-col">
                <button disabled={i === 0} onClick={() => wrap(() => api.post(`/restaurant/categories/${c.id}/move`, null, { params: { direction: "up" } }))} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`category-up-${c.id}`}><ArrowUp className="h-4 w-4" /></button>
                <button disabled={i === items.length - 1} onClick={() => wrap(() => api.post(`/restaurant/categories/${c.id}/move`, null, { params: { direction: "down" } }))} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`category-down-${c.id}`}><ArrowDown className="h-4 w-4" /></button>
              </div>
              {editing?.id === c.id ? (
                <form className="flex flex-1 gap-2" onSubmit={(e) => { e.preventDefault(); wrap(() => api.patch(`/restaurant/categories/${c.id}`, { name: editing.name, is_active: c.is_active }), "Saved").then(() => setEditing(null)); }}>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="category-edit-input" autoFocus />
                  <Button type="submit" size="sm" data-testid="category-save-btn">Save</Button>
                </form>
              ) : <p className="flex-1 font-medium text-slate-800">{c.name}</p>}
              <Switch checked={c.is_active} onCheckedChange={(v) => wrap(() => api.patch(`/restaurant/categories/${c.id}`, { name: c.name, is_active: v }))} data-testid={`category-active-${c.id}`} />
              <button onClick={() => setEditing({ id: c.id, name: c.name })} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid={`category-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
              <ConfirmButton title={`Delete "${c.name}"?`} onConfirm={() => wrap(() => api.delete(`/restaurant/categories/${c.id}`), "Deleted")} className="p-1.5 text-slate-400 hover:text-rose-600" testId={`category-delete-${c.id}`}><Trash2 className="h-4 w-4" /></ConfirmButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
